import baseUrl from '../json/baseURL.json';
import { getAccessToken } from '../../maslConfig';
import { enqueueSnackbar } from 'notistack';
import { AgentMarketPlaceItemWithFilter } from '../../pages/userAgent/agentMarketPlace/AgentMarketPlace';

export const getEnvironmentFromUrl = (): string => {
  const url = baseUrl.baseUrl[0].url;

  if (url.includes('staging')) {
    return 'staging';
  } else if (url.includes('dev')) {
    return 'dev';
  } else if (url.includes('api.waip.wiprocms.com')) {
    return 'prod';
  } else {
    throw new Error('Unable to determine environment from URL');
  }
};

// Reusable function for making API calls
const makeApiCall = async (url: string, payload: any) => {
  const accessToken = await getAccessToken(); // Get a valid token
  if (!accessToken) {
    enqueueSnackbar('Session expired, Please login again', {
      variant: 'error',
    });
    return null;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const message = await response.json();
      if (response.status === 401) {
        enqueueSnackbar(message.message, {
          variant: 'error',
        });
        return null;
      }
      throw new Error(message.message || 'Unexpected error occurred');
    }

    return await response.json();
  } catch (error) {
    enqueueSnackbar('System error: Please try again.', { variant: 'error' });
    return null;
  }
};

export const ingestImages = async (partyId: string, files: File[]) => {
  const url = `${baseUrl.baseUrl[0].url}/v1.1/agent_chat_session/script_agent/${partyId}/ingest`;
  const accessToken = await getAccessToken(); // Get a valid token
  if (!accessToken) {
    enqueueSnackbar('Session expired, Please login again', {
      variant: 'error',
    });
    return null;
  }

  try {
    // Create FormData and append files
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`file${index}`, file);
    });

    // Make the API call
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`, // Add Bearer token
      },
      body: formData, // Send FormData as the request body
    });

    if (!response.ok) {
      const message = await response.json();
      if (response.status === 401) {
        enqueueSnackbar(message.message, {
          variant: 'error',
        });
        return null;
      }
      throw new Error(message.message || 'Unexpected error occurred');
    }

    return await response.json(); // Return the response data
  } catch (error) {
    enqueueSnackbar('System error: Please try again.', { variant: 'error' });
    return null;
  }
};

const getDatasetId = (agentDetails: AgentMarketPlaceItemWithFilter, environment: string): string | null => {
  if (agentDetails.supports_dataset === null || agentDetails.supports_dataset === 'new' || !agentDetails.datasetIds) {
    return null;
  }

  return agentDetails.datasetIds[environment] || null;
};

// askAI function
export const askAI = async (
  userMessage: string,
  agentDetails: AgentMarketPlaceItemWithFilter,
  setAgentResponse: Function,
  setIsLoading: Function,
  uuID: string,
  files: string[]
) => {
  setIsLoading(true);
  const url = `${baseUrl.baseUrl[0].url}/script_agent_orchestrator/v1/agent/query`;

  const environment = getEnvironmentFromUrl();
  const datasetId = getDatasetId(agentDetails, environment);

  const payload = {
    party_id: agentDetails._id,
    message: userMessage,
    session_id: uuID,
    ...(datasetId && { dataset_id: datasetId }),
    ...(files.length > 0 && { files }),
  };

  const data = await makeApiCall(url, payload);

  if (data) {
    // Add assistant's response to the chat
    if (Array.isArray(data) && data.length > 0) {
      const responses = data
        .filter((message: any) => message.role !== 'user' && message.content !== userMessage)
        .map((message: any) => ({ source: message.source, content: message.content }));
      setAgentResponse(responses);
    } else if (data && data.content) {
      setAgentResponse({ source: data.source, content: data.content });
    } else {
      enqueueSnackbar('Unexpected API response format', { variant: 'error' });
    }
  }
  setIsLoading(false);
};

// askPlatformTeams function
export const askPlatformTeams = async (
  userMessage: string,
  agentDetails: AgentMarketPlaceItemWithFilter,
  setAgentResponse: Function,
  setIsLoading: Function,
  isTeamResponsePending: any,
  conversationIDRef: any,
  initials: string,
  files: string[]
) => {
  setIsLoading(true);
  const url = `${baseUrl.baseUrl[0].url2}/v1.1/agent_chat_session/query`;
  const payload = {
    party_id: agentDetails._id,
    party_type: agentDetails.party_type,
    messages: isTeamResponsePending.current ? [] : [{ content: userMessage, role: 'user', name: initials }],
    stream_response: false,
    save_conversation: true,
    ...(conversationIDRef.current && {
      conversation_id: conversationIDRef.current,
    }),
    ...(files.length > 0 && { files }),
  };

  const data = await makeApiCall(url, payload);

  if (data) {
    // Extract and store conversation_id from the first response
    if (!conversationIDRef.current && data.data.conversation_id) {
      conversationIDRef.current = data.data.conversation_id;
    }

    // Add assistant's response to the chat
    if (data && data.data.content) {
      setAgentResponse({ source: 'assistant', content: data.data.content });

      if (data.data.response_status !== 'Completed') {
        isTeamResponsePending.current = true;
        await askPlatformTeams(
          userMessage,
          agentDetails,
          setAgentResponse,
          setIsLoading,
          isTeamResponsePending,
          conversationIDRef,
          initials,
          files
        );
      }
    }
  }

  isTeamResponsePending.current = false;
  setIsLoading(false);
};

export const CreateDeveloperKey = async (name: string) => {
  const accessToken = await getAccessToken();

  if (!accessToken) {
    enqueueSnackbar('Session expired, Please login again', { variant: 'error' });
    return null;
  }

  const url = `${baseUrl.baseUrl[0].url}/v1.1/apikey/trial`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ name: name }),
    });

    return response;
  } catch (error) {
    console.error('Error creating developer key:', error);
    enqueueSnackbar('System error: Please try again.', { variant: 'error' });
    return null;
  }
};

export const getUserKey = async () => {
  const url = `${baseUrl.baseUrl[0].url}/v1.1/apikey/trial`;
  const accessToken = await getAccessToken();

  if (!accessToken) {
    enqueueSnackbar('Session expired, Please login again', {
      variant: 'error',
    });
    return null;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const message = await response.json();
      if (response.status === 401) {
        enqueueSnackbar(message.message, {
          variant: 'error',
        });
        return null;
      }
      throw new Error(message.message || 'Unexpected error occurred');
    }

    return await response.json();
  } catch (error) {
    enqueueSnackbar('System error: Please try again.', { variant: 'error' });
    return null;
  }
};

export const getAgentList = async () => {
  const url = `${baseUrl.baseUrl[0].url}/script_agent_orchestrator/script_agent/marketplace`;
  const accessToken = await getAccessToken();

  if (!accessToken) {
    enqueueSnackbar('Session expired, Please login again', {
      variant: 'error',
    });
    return null;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const message = await response.json();
      if (response.status === 401) {
        enqueueSnackbar(message.message, {
          variant: 'error',
        });
        return null;
      }
      throw new Error(message.message || 'Unexpected error occurred');
    }

    return await response.json();
  } catch (error) {
    enqueueSnackbar('System error: Please try again.', { variant: 'error' });
    return null;
  }
};

export const deleteApiKey = async (apiKeyId: string) => {
  const url = `${baseUrl.baseUrl[0].url}/v1.1/apikey/trial`;
  const accessToken = await getAccessToken(); // Get a valid token

  if (!accessToken) {
    enqueueSnackbar('Session expired, Please login again', {
      variant: 'error',
    });
    return null;
  }

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ api_key: apiKeyId }),
    });

    if (!response.ok) {
      const errorData = await response.text();
      if (response.status === 401) {
        enqueueSnackbar('Authentication failed. Please log in again.', {
          variant: 'error',
        });
        return null;
      }
      throw new Error(errorData || 'Failed to revoke API key');
    }

    return response;
  } catch (error) {
    console.error('Error deleting API key:', error);
    enqueueSnackbar('System error: Please try again.', { variant: 'error' });
    return null;
  }
};

export const downloadAgent = async (partyId: string, agentName: string) => {
  const url = `${baseUrl.baseUrl[0].url}/script_agent_orchestrator/script_agent/${partyId}/download`;
  const accessToken = await getAccessToken();

  if (!accessToken) {
    enqueueSnackbar('Session expired, Please login again', { variant: 'error' });
    return;
  }
  enqueueSnackbar(`Downloading ${agentName} for you.`);
  try {
    // Using fetch with blob response type to handle binary data
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        enqueueSnackbar('Session expired, Please login again', { variant: 'error' });
        return;
      }
      throw new Error(`Failed to download agent: ${response.statusText}`);
    }

    // Get the blob data from response
    const blob = await response.blob();

    // Sanitize agent name for filename (replace spaces and special chars)
    const sanitizedName = agentName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();

    // Use agent name as filename, fallback to default if needed
    const filename = sanitizedName ? `${sanitizedName}.zip` : 'agent.zip';

    // Create download link and trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Clean up
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(link);

    enqueueSnackbar(`Downloaded ${agentName}`, { variant: 'success' });
  } catch (error) {
    console.error('Error downloading agent:', error);
    enqueueSnackbar('Failed to download agent', { variant: 'error' });
  }
};
